import React from 'react';
import { useTranslation } from 'react-i18next';
import { Form, FormikContextType, FormikProvider } from 'formik';
import { Box, Typography } from '@mui/material';
import FormikTextField from '../../../components/formikComponents/FormikTextField';
import FormikCheckbox from '../../../components/formikComponents/FormikCheckbox';
import SideFormActions from '../../../components/createEditComponents/SideFormActions';
import { STAFF_ROLES_CACHE_KEY } from '../../../constants';
import { StaffRoleErrors } from '../../../types';
import { setFieldOnSessionStorage } from '../../../helpers/helpers';
import AdminTillAccessForm from './AdminTillAccessForm';
import ItemDiscountForm from './ItemDiscountForm';
import useBusinessInformation from '../../../helperHooks/useBusinessInformation';
import BasketDiscountForm from './BasketDiscountForm';
import RefundsForm from './RefundsForm';
import StockOptionsForm from './StockOptionsForm';

type StaffRoleFormProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formik: FormikContextType<any>;
  cancelAction: () => void;
  objectId: string;
  isSubmitting: boolean;
  errors: StaffRoleErrors;
  clearError: (errorKey: string) => void;
  addMarginBottom?: boolean;
};

export const handleAccordionCheckboxChange =
  (formik: FormikContextType<unknown>, objectId: string, inputOptions: string[]) =>
  (event: React.ChangeEvent<HTMLInputElement>) => {
    inputOptions.forEach((option) => {
      formik.setFieldValue(option, event.target.checked);
      setFieldOnSessionStorage(STAFF_ROLES_CACHE_KEY, objectId, option, event.target.checked);
    });
  };

const StaffRoleForm = ({
  formik,
  cancelAction,
  objectId,
  isSubmitting,
  errors,
  clearError,
  addMarginBottom = false,
}: StaffRoleFormProps) => {
  const { t } = useTranslation();
  const { currencySymbol } = useBusinessInformation();

  return (
    <FormikProvider value={formik}>
      <Form onSubmit={formik.handleSubmit}>
        <Box
          sx={{
            ...(addMarginBottom && {
              marginBottom: '65px',
            }),
          }}
        >
          <FormikTextField
            label={t('screens.staffRoles.name')}
            id="name"
            required
            invalidPattern={/[#']/g}
            objectId={objectId}
            formName={STAFF_ROLES_CACHE_KEY}
            name="name"
            error={errors?.name}
            clearError={clearError}
          />
          <FormikTextField
            label={t('screens.staffRoles.description')}
            id="description"
            required
            invalidPattern={/[#']/g}
            objectId={objectId}
            formName={STAFF_ROLES_CACHE_KEY}
            name="description"
            error={errors?.description}
            clearError={clearError}
          />

          <FormikCheckbox
            label={t('screens.staffRoles.allow_global_TillAccess')}
            id="globalTillAccess"
            name="globalTillAccess"
            type="checkbox"
            formName={STAFF_ROLES_CACHE_KEY}
            objectId={objectId}
          />
          {formik.values.globalTillAccess && (
            <Box
              sx={{
                marginTop: '0.5rem',
                paddingTop: '1rem',
                paddingLeft: 0,
                borderTop: (theme) =>
                  `1px ${
                    theme.palette.mode === 'light'
                      ? theme?.palette?.grey?.['12p']
                      : theme?.palette?.grey?.['23p']
                  } solid`,
              }}
            >
              <Typography sx={{ paddingBottom: '0.5rem' }}>Till Permissions: </Typography>
              <AdminTillAccessForm formik={formik} objectId={objectId} />
              <StockOptionsForm formik={formik} objectId={objectId} />
              <ItemDiscountForm
                formik={formik}
                objectId={objectId}
                currency={currencySymbol}
                errors={errors}
                clearError={clearError}
              />
              <BasketDiscountForm
                formik={formik}
                objectId={objectId}
                currency={currencySymbol}
                errors={errors}
                clearError={clearError}
              />
              <RefundsForm
                formik={formik}
                objectId={objectId}
                currency={currencySymbol}
                errors={errors}
                clearError={clearError}
              />
            </Box>
          )}
        </Box>
        <SideFormActions cancelAction={cancelAction} isSubmitting={isSubmitting} />
      </Form>
    </FormikProvider>
  );
};
export default StaffRoleForm;
